import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Building2,
  Ticket,
  Trophy,
  User,
  LogIn,
  Bell,
} from 'lucide-react';
import {
  DesktopNav,
  MobileNav,
  Button,
  Avatar,
  useViewport,
  type NavItem,
} from '@raffle/ui';
import { useAuth } from '../../providers/AuthProvider';

const navItems: NavItem[] = [
  { id: 'home', label: 'Home', href: '/', icon: <Home className="w-full h-full" /> },
  { id: 'properties', label: 'Properties', href: '/properties', icon: <Building2 className="w-full h-full" /> },
  { id: 'tickets', label: 'My Tickets', href: '/tickets', icon: <Ticket className="w-full h-full" /> },
  { id: 'draws', label: 'Draws', href: '/draws', icon: <Trophy className="w-full h-full" /> },
  { id: 'profile', label: 'Profile', href: '/profile', icon: <User className="w-full h-full" /> },
];

const Logo: React.FC = () => (
  <a href="/" className="flex items-center gap-2">
    <div className="w-10 h-10 rounded-vault bg-gradient-to-br from-[#008751] to-gold-500 flex items-center justify-center relative overflow-hidden">
      {/* Mini Nigerian flag accent */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
      <span className="text-white font-bold text-sm">NV</span>
    </div>
    <span className="font-display text-xl font-bold text-text-primary hidden sm:block">
      Naija<span className="text-gold-500">Vault</span>
    </span>
  </a>
);

export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useViewport();
  const { user, isAuthenticated } = useAuth();

  const getActiveItem = (): string => {
    const path = location.pathname;
    if (path === '/') return 'home';
    const item = navItems.find((item) => path.startsWith(item.href) && item.href !== '/');
    return item?.id || 'home';
  };

  const handleNavClick = (item: NavItem) => {
    navigate(item.href);
  };

  const RightContent = () => (
    <div className="flex items-center gap-3">
      {isAuthenticated ? (
        <>
          <button className="relative p-2 rounded-full hover:bg-white/5 transition-colors">
            <Bell className="w-5 h-5 text-text-secondary" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-vault-error rounded-full" />
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2"
          >
            <Avatar
              src={user?.avatarUrl}
              alt={`${user?.firstName} ${user?.lastName}`}
              fallback={`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
              size="sm"
            />
            {isDesktop && (
              <span className="text-sm font-medium text-text-primary">
                {user?.firstName}
              </span>
            )}
          </button>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/login')}
          >
            <LogIn className="w-4 h-4 mr-2" />
            Sign In
          </Button>
          <Button
            variant="gold"
            size="sm"
            onClick={() => navigate('/register')}
          >
            Get Started
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="page-container">
      {/* Desktop Navigation */}
      {isDesktop && (
        <DesktopNav
          logo={<Logo />}
          items={navItems}
          activeItem={getActiveItem()}
          onItemClick={handleNavClick}
          rightContent={<RightContent />}
        />
      )}

      {/* Mobile Top Bar (auth actions) */}
      {!isDesktop && (
        <header className="sticky top-0 z-40 bg-surface-0/95 backdrop-blur-sm border-b border-white/5">
          <div className="content-wrapper h-16 flex items-center justify-between">
            <Logo />
            <RightContent />
          </div>
        </header>
      )}

      {/* Page Content */}
      <main className={isDesktop ? 'pt-nav-desktop' : 'pt-16 pb-nav-mobile'}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Mobile Navigation */}
      {!isDesktop && (
        <MobileNav
          items={navItems}
          activeItem={getActiveItem()}
          onItemClick={handleNavClick}
        />
      )}
    </div>
  );
};
